// Access Control Service for AgriManage Pro
// Manages user permissions, company access, and page-level security

import { db } from '../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

class AccessControlService {
  constructor() {
    this.defaultPermissions = {
      manager: {
        pages: ['all'],
        companies: ['all'],
        actions: ['all']
      },
      accountant: {
        pages: ['dashboard', 'parcels', 'finances', 'arenda', 'inventory', 'settings'],
        companies: [], // Will be set per user
        actions: ['view', 'edit', 'create']
      },
      worker: {
        pages: ['dashboard', 'fields', 'tasks', 'weather', 'worker', 'inventory'],
        companies: [], // Will be set per user
        actions: ['view', 'edit']
      }
    };
  }

  // Get default permissions for a role
  getDefaultPermissions(role) {
    return { ...this.defaultPermissions[role] } || this.defaultPermissions.worker;
  }

  // Check if user has access to a specific page
  hasPageAccess(user, page) {
    if (!user || !user.permissions) return false;
    
    // Managers have access to everything
    if (user.role === 'manager') return true;
    
    // Check if user has specific page permission
    const userPages = user.permissions.pages || [];
    return userPages.includes('all') || userPages.includes(page);
  }

  // Check if user has access to a specific company's data
  hasCompanyAccess(user, company) {
    if (!user || !user.permissions) return false;
    
    // Managers have access to all companies
    if (user.role === 'manager') return true;
    
    // Check if user has access to this company
    const userCompanies = user.permissions.companies || [];
    return userCompanies.includes('all') || userCompanies.includes(company);
  }

  // Filter data based on user's company access
  filterDataByCompany(user, data, companyField = 'firma') {
    if (!user || !user.permissions) return [];
    
    // Managers see all data
    if (user.role === 'manager') return data;
    
    const userCompanies = user.permissions.companies || [];
    if (userCompanies.includes('all')) return data;
    
    // Filter data to only show user's companies
    return data.filter(item => {
      if (Array.isArray(item)) {
        // Handle arrays of data
        return item.some(subItem => 
          userCompanies.includes(subItem[companyField])
        );
      } else if (item && typeof item === 'object') {
        // Handle single objects
        return userCompanies.includes(item[companyField]);
      }
      return false;
    });
  }

  // Get available pages for a user
  getAvailablePages(user) {
    if (!user || !user.permissions) return [];
    
    const allPages = [
      { id: 'dashboard', name: 'Dashboard', icon: '📊' },
      { id: 'fields', name: 'Fields', icon: '🌾' },
      { id: 'weather', name: 'Weather', icon: '🌤️' },
      { id: 'tasks', name: 'Tasks', icon: '✅' },
      { id: 'inventory', name: 'Inventory', icon: '📦' },
      { id: 'finances', name: 'Finances', icon: '💰' },
      { id: 'parcels', name: 'Parcel Manager', icon: '🗺️' },
      { id: 'arenda', name: 'Arenda Manager', icon: '🌾' },
      { id: 'seasons', name: 'Season Management', icon: '📅' },
      { id: 'stock', name: 'Stock Adjustment', icon: '📋' },
      { id: 'planning', name: 'Work Planning', icon: '📋' },
      { id: 'worker', name: 'Worker Interface', icon: '👷' },
      { id: 'settings', name: 'Settings', icon: '⚙️' }
    ];

    // Managers see all pages
    if (user.role === 'manager') return allPages;

    // Filter pages based on user permissions
    const userPages = user.permissions.pages || [];
    if (userPages.includes('all')) return allPages;

    return allPages.filter(page => userPages.includes(page.id));
  }

  // Get all available pages for permission management
  getAllPages() {
    return [
      { id: 'dashboard', name: 'Dashboard', icon: '📊' },
      { id: 'fields', name: 'Câmpuri', icon: '🌾' },
      { id: 'tasks', name: 'Sarcini', icon: '📋' },
      { id: 'inventory', name: 'Inventar', icon: '📦' },
      { id: 'finances', name: 'Finanțe', icon: '💰' },
      { id: 'parcels', name: 'Parcel Manager', icon: '🗺️' },
      { id: 'seasons', name: 'Season Management', icon: '🌱' },
      { id: 'stockadjustment', name: 'Reglare Stocuri', icon: '📊' },
      { id: 'workplanning', name: 'Planificare Lucrări', icon: '📅' },
      { id: 'arendamanager', name: 'Arenda Manager', icon: '🏠' },
      { id: 'weather', name: 'Meteo', icon: '🌤️' },
      { id: 'settings', name: 'Setări', icon: '⚙️' }
    ];
  }

  // Update user permissions
  async updateUserPermissions(userId, newPermissions) {
    console.log('💾 AccessControl: Updating permissions for user:', userId);
    console.log('📝 New permissions:', newPermissions);
    
    try {
      // Validate permissions first
      this.validatePermissions(newPermissions);
      
      const farmId = localStorage.getItem('currentFarmId');
      console.log('🏢 Farm ID for save:', farmId);
      
      if (!farmId || !userId) {
        throw new Error('Farm ID or User ID missing');
      }

      // Save permissions to Firebase
      const firebasePath = `farms/${farmId}/userPermissions/${userId}`;
      console.log('💾 Saving to Firebase path:', firebasePath);
      
      await setDoc(doc(db, firebasePath), newPermissions);
      console.log('✅ Permissions saved successfully to Firebase');
      
      return { uid: userId, permissions: newPermissions };
    } catch (error) {
      console.error('❌ Error updating user permissions:', error);
      throw error;
    }
  }

  // Get user permissions
  async getUserPermissions(userId) {
    console.log('🔍 AccessControl: Getting permissions for user:', userId);
    
    try {
      const farmId = localStorage.getItem('currentFarmId');
      console.log('🏢 Farm ID:', farmId);
      
      if (!farmId || !userId) {
        console.log('❌ Missing farmId or userId, returning default permissions');
        return this.getDefaultPermissions('worker');
      }

      // Try to load permissions from Firebase
      console.log('🔄 Loading from Firebase path:', `farms/${farmId}/userPermissions/${userId}`);
      const userPermissionsDoc = await getDoc(doc(db, `farms/${farmId}/userPermissions/${userId}`));
      
      if (userPermissionsDoc.exists()) {
        const permissions = userPermissionsDoc.data();
        console.log('✅ Found permissions in Firebase:', permissions);
        return permissions;
      } else {
        console.log('⚠️ No permissions found in Firebase, returning default permissions');
      }
      
      // If no permissions found in Firebase, return default permissions
      const defaultPerms = this.getDefaultPermissions('worker');
      console.log('📋 Default permissions:', defaultPerms);
      return defaultPerms;
    } catch (error) {
      console.error('❌ Error getting user permissions:', error);
      return this.getDefaultPermissions('worker');
    }
  }

  // Create user with specific permissions
  createUserWithPermissions(userData, permissions) {
    const defaultPerms = this.getDefaultPermissions(userData.role);
    return {
      ...userData,
      permissions: {
        ...defaultPerms,
        ...permissions
      }
    };
  }

  // Validate permissions object
  validatePermissions(permissions) {
    const requiredFields = ['pages', 'companies', 'actions'];
    
    for (const field of requiredFields) {
      if (!permissions[field] || !Array.isArray(permissions[field])) {
        throw new Error(`Invalid permissions: ${field} must be an array`);
      }
    }
    
    return true;
  }

  // Get company list for permission assignment
  getAvailableCompanies() {
    // This would typically come from Firebase
    // For now, get from localStorage
    try {
      const farmId = localStorage.getItem('currentFarmId');
      if (farmId) {
        const companySettings = localStorage.getItem(`companySettings_${farmId}`);
        if (companySettings) {
          const parsed = JSON.parse(companySettings);
          return parsed.data?.companies || [];
        }
      }
      return [];
    } catch (error) {
      console.error('Error loading companies:', error);
      return [];
    }
  }
}

// Export singleton instance
const accessControlService = new AccessControlService();
export default accessControlService; 